import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser, Check } from 'lucide-react';

const ManualMaskTool = ({ imageSrc, onMaskGenerated }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(30);

  useEffect(() => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const container = containerRef.current;
      if (!container) return;
      
      const width = container.clientWidth;
      const height = (img.naturalHeight / img.naturalWidth) * width;
      
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      
      canvas.width = width;
      canvas.height = height;
      maskCanvas.width = width;
      maskCanvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Initialize mask canvas with transparent background
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.clearRect(0, 0, width, height);
    };
  }, [imageSrc]);

  const getCoordinates = (e) => {
    const canvas = maskCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX || (e.touches && e.touches[0].clientX);
    const y = e.clientY || (e.touches && e.touches[0].clientY);
    return {
      x: x - rect.left,
      y: y - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    draw(e);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const maskCtx = maskCanvasRef.current.getContext('2d');
    maskCtx.beginPath();
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const { x, y } = getCoordinates(e);
    const maskCtx = maskCanvasRef.current.getContext('2d');
    
    maskCtx.lineWidth = brushSize;
    maskCtx.lineCap = 'round';
    maskCtx.lineJoin = 'round';
    // Draw in pure opaque black. It will be rendered as red overlay via CSS.
    maskCtx.strokeStyle = 'rgba(0, 0, 0, 1)';
    
    // Add feathering to the brush
    maskCtx.shadowBlur = brushSize / 2;
    maskCtx.shadowColor = 'rgba(0, 0, 0, 1)';
    
    maskCtx.lineTo(x, y);
    maskCtx.stroke();
    maskCtx.beginPath();
    maskCtx.moveTo(x, y);
  };

  const handleClear = () => {
    const maskCanvas = maskCanvasRef.current;
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
  };

  const handleApply = () => {
    const maskCanvas = maskCanvasRef.current;
    const dataUrl = maskCanvas.toDataURL('image/png');
    onMaskGenerated(dataUrl);
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex items-center justify-between bg-bg-secondary p-3 rounded-sm border border-border-color">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-sans text-text-secondary">Brush Size:</label>
          <input 
            type="range" 
            min="10" 
            max="100" 
            value={brushSize} 
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-24 md:w-32"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={handleClear} className="text-text-muted hover:text-text-primary">
          <Eraser className="w-4 h-4 mr-2" />
          Clear
        </Button>
      </div>

      <div 
        ref={containerRef} 
        className="relative w-full rounded-sm overflow-hidden border border-border-color cursor-crosshair touch-none"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      >
        <canvas ref={canvasRef} className="block w-full h-auto" />
        <canvas 
          ref={maskCanvasRef} 
          className="absolute inset-0 w-full h-full pointer-events-none opacity-50"
          style={{ mixBlendMode: 'multiply', filter: 'sepia(1) hue-rotate(-50deg) saturate(10) brightness(0.8)' }}
        />
      </div>
      
      <p className="text-sm text-text-muted text-center font-sans">
        Paint over your hair to create a mask. The red overlay shows the painted area.
      </p>

      <Button onClick={handleApply} className="w-full bg-accent-primary text-white hover:bg-accent-hover font-sans tracking-widest text-sm uppercase py-4">
        <Check className="w-4 h-4 mr-2" />
        Apply Mask
      </Button>
    </div>
  );
};

export default ManualMaskTool;
